import type { CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import LogoFull from './LogoFull';
import LogoIcon from './LogoIcon';
import { materialIcon } from '../../theme/icons';
import {
  Settings as SidebarSettings,
  UserSettingsSignInAvatar,
} from '@backstage/plugin-user-settings';
import { SidebarSearchModal } from '@backstage/plugin-search';
import {
  Sidebar,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarScrollWrapper,
  SidebarSpace,
  useSidebarOpenState,
} from '@backstage/core-components';
import { NotificationsSidebarItem } from '@backstage/plugin-notifications';
import type { NavContentComponentProps } from '@backstage/plugin-app-react';
import type { IconComponent } from '@backstage/core-plugin-api';
import { environmentSettingsTabs } from '@internal/plugin-environments';
import { MyGroupsNavGroup } from './MyGroupsNavGroup';
import { SidebarNavGroup } from './SidebarNavGroup';

const HomeIcon = materialIcon('home');
const CategoryIcon = materialIcon('category');
const PeopleIcon = materialIcon('person_outline');
const GroupIcon = materialIcon('group');
const ExtensionIcon = materialIcon('api');
const LibraryBooks = materialIcon('article');
const SpeedIcon = materialIcon('show_chart');
const SettingsIcon = materialIcon('tune');
const CreateComponentIcon = materialIcon('add_circle_outline');
const MenuIcon = materialIcon('menu');
const SearchIcon = materialIcon('search');
const SwatchIcon = materialIcon('palette');
const UserSettingsIcon = materialIcon('settings');
const NotificationsIcon = materialIcon('notifications_none');

// DS SidebarNav brand row: 64h, 0 20, ruled off from the nav below.
const logoRootStyle: CSSProperties = {
  width: '100%',
  height: 64,
  flex: '0 0 64px',
  boxSizing: 'border-box',
  display: 'flex',
  alignItems: 'center',
  borderBottom: '1px solid var(--bui-border-1)',
  marginBottom: 'var(--bui-space-3)',
};

const logoLinkStyle: CSSProperties = {
  display: 'flex',
  textDecoration: 'none',
};

const SidebarLogo = () => {
  const { isOpen } = useSidebarOpenState();

  return (
    <div
      style={{
        ...logoRootStyle,
        padding: isOpen ? '0 var(--bui-space-5)' : 0,
        justifyContent: isOpen ? 'flex-start' : 'center',
      }}
    >
      <Link to="/" style={logoLinkStyle} aria-label="Home">
        {isOpen ? <LogoFull /> : <LogoIcon />}
      </Link>
    </div>
  );
};

// Shortcuts land on another section's page (Users is /catalog), and SidebarItem
// matches on pathname only, so let that section's item carry the highlight.
const ShortcutItem = (props: {
  icon: IconComponent;
  to: string;
  text: string;
}) => (
  <SidebarItem {...props} {...{ activeClassName: '', 'aria-current': false }} />
);

// Pages we place by hand below, with our own label and icon.
const CURATED_PAGE_IDS = [
  'page:home',
  'page:catalog',
  'page:api-docs',
  'page:techdocs',
  'page:catalog-graph',
  'page:catalog-import',
  'page:scaffolder',
  'page:search',
  'page:notifications',
  'page:user-settings',
  'page:spog',
  'page:environments',
  'page:environments/legacy-redirect',
  'page:app/controls-playground',
];

export const NavContent = ({ navItems }: NavContentComponentProps) => {
  // Curated items are hard-coded; anything a plugin contributes that we
  // haven't placed lands in the scroll area below. Claiming the ids we render
  // by hand keeps them from showing up a second time down there.
  const discovered = navItems.withComponent(({ href, title, icon }) => (
    <SidebarItem to={href} icon={() => icon} text={title} />
  ));
  for (const id of CURATED_PAGE_IDS) {
    discovered.take(id);
  }

  return (
    /* drawerWidthOpen left at Backstage's default (224). Overriding it
      via sidebarOptions desyncs <SidebarPage>'s reserved padding-left
      (which reads the *default* SidebarConfigContext from above) from
      the rendered drawer width — making the page header slide under
      the drawer. No DOM hook for `isPinned` to fix via CSS without
      breaking the hover-only state, so we accept the 24px width
      divergence from the Figma reference. */
    <Sidebar>
      <SidebarLogo />
      <SidebarGroup label="Search" icon={<SearchIcon />} to="/search">
        {/* Otherwise SidebarSearchModal falls back to MUI's filled Search icon. */}
        <SidebarSearchModal icon={SearchIcon} />
      </SidebarGroup>
      <SidebarDivider />
      <SidebarGroup label="Menu" icon={<MenuIcon />}>
        {/* Global nav, not org-specific */}
        <SidebarItem icon={HomeIcon} to="home" text="Home" />
        <SidebarItem icon={CategoryIcon} to="catalog" text="Catalog" />
        <SidebarItem icon={ExtensionIcon} to="api-docs" text="APIs" />
        <SidebarItem icon={LibraryBooks} to="docs" text="Docs" />
        <SidebarDivider />
        <ShortcutItem
          icon={PeopleIcon}
          to="catalog?filters[kind]=user"
          text="Users"
        />
        <MyGroupsNavGroup icon={GroupIcon} />
        <SidebarDivider />
        {/* Deep-links to the tab rather than /spog so the landing redirect
          doesn't flash on every click. */}
        <SidebarItem
          icon={SpeedIcon}
          to="spog/load_testing"
          text="Performance Testing"
        />
        <SidebarNavGroup
          icon={SettingsIcon}
          text="Environments"
          to={environmentSettingsTabs[0].href}
          items={environmentSettingsTabs}
        />
        {/* Registered only in dev builds; see appModule.tsx. */}
        {process.env.NODE_ENV !== 'production' && (
          <SidebarItem icon={SwatchIcon} to="controls" text="Controls" />
        )}
        <SidebarItem icon={CreateComponentIcon} to="create" text="Create..." />
        {/* End global nav */}
        <SidebarDivider />
        <SidebarScrollWrapper>
          {discovered.rest({ sortBy: 'title' })}
        </SidebarScrollWrapper>
      </SidebarGroup>
      <SidebarSpace />
      <SidebarDivider />
      <NotificationsSidebarItem icon={NotificationsIcon} />
      <SidebarDivider />
      <SidebarGroup
        label="Settings"
        icon={<UserSettingsSignInAvatar />}
        to="/settings"
      >
        <SidebarSettings icon={UserSettingsIcon} />
      </SidebarGroup>
    </Sidebar>
  );
};
